// Launching a mission run — the single door on the app side (CCT-286).
//
// Every path that makes a mission playable goes through `launch()`: starting a trial, opening a
// bought mission, opening a company mission. It asks for the play language when there is a real
// choice, calls `POST /me/stories/{id}/start`, and only then navigates.
//
// ⚠️ THE LANGUAGE IS ASKED BEFORE ENTERING, BECAUSE IT CANNOT BE ASKED AFTER. It is frozen on
// the run: a chapter already played and the next one must be in the same language. Offering the
// choice inside the mission would be offering something the server will refuse.
import React, { useCallback, useState } from 'react';
import { ActivityIndicator, Modal, StyleSheet, Text, TouchableOpacity, View } from 'react-native';

import { fetchMissionControllers, fetchMissionRun, fetchPlayLanguages, startMissionRun } from '../api/missionRun';
import { useNavigation } from '@react-navigation/native';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { getThemeColors } from '../utils/themeStyles';
import { LANGUAGE_NAMES } from '../i18n';
import { useControllersBlocked } from '../components/ControllersBlocked';
import { useT } from '../i18n/useT';

/** ⚠️ LE DIALOGUE NE SERT QU'AUX PARTIES NON DÉMARRÉES. Une partie en cours a sa langue, et
 *  elle ne se rejoue pas : rien à porter ici sur l'existant. */
type Pending = {
  missionId: number;
  languages: string[];
  onDone: () => void;
  /**
   * Éléments déjà terminés sur une partie JAMAIS démarrée — le résidu d'avant CCT-286, ou de la
   * période où `POST /start` rendait 500 sans que personne le sache.
   *
   * ⚠️ DÉMARRER LES EFFACE, ET IL FAUT LE DIRE AVANT. Le porteur a démarré une nouvelle mission
   * et retrouvé ses tâches marquées DONE : le contraire est tout aussi mauvais — effacer sans
   * prévenir. On l'annonce dans le dialogue, sans ajouter d'étape.
   */
  residue: number;
} | null;

export function useMissionLaunch() {
  const t = useT();
  const { getAccessToken } = useAuth();
  const { isDarkTheme } = useTheme();
  const colors = getThemeColors(isDarkTheme);

  const [pending, setPending] = useState<Pending>(null);
  const [busy, setBusy] = useState(false);
  //: ⚠️ THE WAIT MUST BE SEEN, AND MUST BLOCK. `launch` chains three network calls (controllers,
  //: languages, run state) then starts the engine. Nothing was displayed during that time: the
  //: player clicked again, which started a SECOND synchronisation, and the page only opened at
  //: the end. A modal makes the click immediately visible and the second one impossible.
  const [preparing, setPreparing] = useState(false);
  const [failed, setFailed] = useState(false);
  /** Contrôleurs manquants : la mission ne s'ouvre pas, et on dit lesquels. */
  const navigation = useNavigation<any>();
  //: ⚠️ THE REFUSAL DIALOG IS SHARED (CCT-388), not owned here. Every moment a controller is
  //: found missing — launch, hand-over — must say it in the SAME words and lead to the SAME
  //: place; the version that lived here could only be raised by the launch.
  const { modal: blocage, bloquer, bloque: blocked } = useControllersBlocked();

  const run = useCallback(async (missionId: number, language: string | null,
                                 onDone: () => void) => {
    setBusy(true);
    // ⚠️ JAMAIS DE `restart` DEPUIS CE CHEMIN. Relancer efface la progression : cela se demande
    // ailleurs, explicitement, pas au détour d'une ouverture de mission.
    const res = await startMissionRun(getAccessToken(), missionId, language);
    setBusy(false);
    setPending(null);
    // ⚠️ UN REFUS DE CONTRÔLEURS ARRÊTE LA NAVIGATION — c'est la décision produit (CCT-330) :
    // rien ne commence tant que TOUS les accès requis ne sont pas accordés. La pré-lecture
    // ci-dessus l'anticipe, mais elle peut être contournée (lien direct, rechargement, état
    // illisible) : c'est ce chemin-ci qui répond alors, avec la réponse du serveur.
    if (!res.started && res.reason === 'require_controllers') {
      bloquer(missionId, res.controllers ?? []);
      return;
    }
    setFailed(!res.started);
    if (!res.started) return;
    onDone();
  }, [getAccessToken, bloquer]);

  /**
   * Starts (or resumes) a mission, then runs `onDone` — usually the navigation.
   *
   * Asks for a language only when there is more than one AND nothing is running yet. A run
   * already started ignores the argument server-side, so re-asking would suggest a choice that
   * no longer exists.
   */
  const lancer = useCallback(async (missionId: number, onDone: () => void) => {
    // ⚠️ LES CONTRÔLEURS D'ABORD, ET ON N'ENTRE PAS SANS EUX (CCT-328). Le serveur refuse déjà
    // le démarrage — c'est LUI la garde — mais le laisser refuser sans rien anticiper produirait
    // une erreur brute au clic. On lit l'état, et si quelque chose manque on le DIT, sans
    // démarrer ni naviguer.
    //
    // Une lecture impossible ne bloque pas : ne pas savoir si un contrôleur est prêt n'est pas
    // savoir qu'il ne l'est pas, et le serveur reste de toute façon la dernière porte.
    try {
      const etat = await fetchMissionControllers(getAccessToken(), missionId);
      if (etat && etat.allReady === false) {
        bloquer(missionId, etat.controllers.filter((c) => !c.ready).map((c) => c.kind));
        return;
      }
    } catch {
      /* état illisible : on laisse le serveur trancher */
    }
    // ⚠️ ON LIT L'ÉTAT DE LA PARTIE AVANT DE PROPOSER QUOI QUE CE SOIT. La liste des langues est
    // PUBLIQUE — le choix précède l'inscription — donc elle ignore qu'une partie tourne déjà.
    // L'app posait la même question à la première ouverture et à la douzième, et le serveur
    // ignorait la réponse dès la deuxième : le joueur choisissait l'anglais, la mission restait
    // en français, rien ne le disait. C'est le défaut signalé.
    const [info, state] = await Promise.all([
      fetchPlayLanguages(missionId),
      fetchMissionRun(getAccessToken(), missionId),
    ]);
    const langs = info?.languages ?? [];
    // ⚠️ UNE PARTIE EN COURS N'A PLUS DE LANGUE À CHOISIR — ON OUVRE, POINT. Le dialogue
    // s'affichait dès qu'une mission proposait plus d'une langue, y compris sur une partie déjà
    // lancée : il montrait trois boutons dont l'un marqué « partie en cours », c'est-à-dire un
    // choix DÉJÀ FAIT et que le serveur refuse de rejouer. Proposer une décision qu'on ne peut
    // pas honorer est la même faute que la question dont la réponse était ignorée, en plus
    // visible. Rejouer dans une autre langue est un geste à part — il relance la mission et
    // efface la progression — et n'a rien à faire sur le chemin d'ouverture.
    if (state?.started) {
      // ⚠️ A RUNNING GAME HAS NOTHING TO RESTART. This path posted the start anyway: two gates,
      // an assignment resume, an engine wake, and above all the voice RE-PLANNING, which reloads
      // and parses the whole scenario. That was the long wait on every opening, for a game that
      // was already running.
      //
      // ⚠️ UNLESS THE ENGINE SLEEPS WHILE A NUDGE IS CONFIGURED. The server says so, because it
      // alone knows the setting and the job status. When in doubt it answers yes and we go back
      // through the start: better a wait than a nudge with no engine behind it.
      // ⚠️ `=== false`, NOT `!`. An ABSENT field is an older server that cannot answer:
      // `!undefined` is true, so the negation would have skipped the start EVERYWHERE, including
      // where the engine sleeps with a nudge configured. Doubt must cost a wait, never a silent
      // nudge.
      if (state.engineWakeNeeded === false) { onDone(); return; }
      await run(missionId, null, onDone);
      return;
    }
    if (langs.length > 1) {
      setPending({ missionId, languages: langs, onDone,
                   residue: state?.completedItems ?? 0 });
      return;
    }
    // Une seule langue (ou serveur muet) : rien à demander, on ne fabrique pas un écran de
    // choix pour un choix qui n'existe pas.
    await run(missionId, langs[0] ?? null, onDone);
  }, [run, getAccessToken]);

  const launch = useCallback(async (missionId: number, onDone: () => void) => {
    if (preparing) return;              // a launch is already running: never stack a second
    setPreparing(true);
    setFailed(false);
    try {
      await lancer(missionId, onDone);
    } catch {
      setFailed(true);
    } finally {
      setPreparing(false);
    }
  }, [preparing, lancer]);

  const dialog = (
    <Modal visible={!!pending} transparent animationType="fade"
           onRequestClose={() => setPending(null)}>
      <View style={st.backdrop}>
        <View style={[st.card, { backgroundColor: colors.cardBackground }]}>
          <Text style={[st.title, { color: colors.textPrimary }]}>{t('launch.title')}</Text>
          <Text style={[st.body, { color: colors.textSecondary }]}>{t('launch.body')}</Text>
          {(pending?.residue ?? 0) > 0 && (
            <Text style={[st.body, { color: colors.warning }]}>
              {t('launch.freshStart', { count: String(pending!.residue) })}
            </Text>
          )}
          <View style={st.options}>
            {(pending?.languages ?? []).map((code) => (
              <TouchableOpacity
                key={code}
                disabled={busy}
                // On n'arrive ici que sur une partie NON démarrée : le choix est libre.
                onPress={() => { void run(pending!.missionId, code, pending!.onDone); }}
                style={[st.option, { borderColor: colors.border, backgroundColor: colors.surfaceSecondary }]}
              >
                {/* Chaque langue nommée DANS SA LANGUE : c'est la seule façon qu'un
                    arabophone reconnaisse la sienne dans une liste rendue en français. */}
                <Text style={[st.optionText, { color: colors.textPrimary }]}>
                  {LANGUAGE_NAMES[code] ?? code}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
          {busy
            ? <View style={st.busy}>
                <ActivityIndicator color={colors.primary} />
                <Text style={{ color: colors.textSecondary, fontSize: 12 }}>{t('launch.starting')}</Text>
              </View>
            : <TouchableOpacity onPress={() => setPending(null)} style={st.cancel}>
                <Text style={{ color: colors.textTertiary }}>{t('common.cancel')}</Text>
              </TouchableOpacity>}
        </View>
      </View>
    </Modal>
  );

  /** BLOCKING WAIT during preparation: the click has an immediate effect, and a second click no
   *  longer has any grip. This is what was missing, and what started two synchronisations. */
  const preparation = (
    <Modal visible={preparing && !pending && !blocked} transparent animationType="fade">
      <View style={st.backdrop}>
        <View style={[st.card, { backgroundColor: colors.cardBackground }]}>
          <View style={st.busy}>
            <ActivityIndicator color={colors.primary} />
            <Text style={{ color: colors.textSecondary, fontSize: 13 }}>
              {t('launch.preparing')}
            </Text>
          </View>
        </View>
      </View>
    </Modal>
  );

  return {
    launch,
    // ⚠️ LES DEUX DIALOGUES VOYAGENT ENSEMBLE. Les écrans montent `dialog` ; en rendre un second
    // séparément aurait obligé chaque appelant à s'en souvenir — et celui qui l'oublie voit sa
    // mission refuser de s'ouvrir SANS rien afficher, ce qui est pire que le défaut d'origine.
    dialog: <>{dialog}{blocage}{preparation}
      <Modal visible={failed} transparent animationType="fade" onRequestClose={() => setFailed(false)}>
        <View style={st.backdrop}><View style={[st.card, { backgroundColor: colors.cardBackground }]}>
          <Text style={{ color: colors.textPrimary }}>{t('launch.unavailable')}</Text>
          <TouchableOpacity style={st.cancel} onPress={() => setFailed(false)}>
            <Text style={{ color: colors.textPrimary }}>{t('common.close')}</Text>
          </TouchableOpacity>
        </View></View>
      </Modal>
    </>,
    launchFailed: failed,
    clearLaunchFailed: () => setFailed(false),
  };
}

const st = StyleSheet.create({
  backdrop: { flex: 1, backgroundColor: 'rgba(0,0,0,0.55)', alignItems: 'center', justifyContent: 'center', padding: 24 },
  card: { borderRadius: 16, padding: 22, width: '100%', maxWidth: 420 },
  title: { fontSize: 18, fontWeight: '700', marginBottom: 8 },
  body: { fontSize: 13, lineHeight: 19, marginBottom: 16 },
  options: { gap: 8 },
  option: { borderWidth: 1, borderRadius: 10, paddingVertical: 12, paddingHorizontal: 14, alignItems: 'center' },
  optionText: { fontSize: 15, fontWeight: '600' },
  busy: { flexDirection: 'row', alignItems: 'center', gap: 8, justifyContent: 'center', marginTop: 16 },
  cancel: { alignItems: 'center', marginTop: 16, padding: 8 },
  btn: { marginTop: 16, borderRadius: 10, paddingVertical: 11, alignItems: 'center' },
  btnText: { color: '#fff', fontWeight: '600', fontSize: 14 },
});
